import { useState, useEffect } from "react";

/**
 * Returns true only after the page's main content has had a chance to become
 * interactive — used to defer mounting of non-critical third-party widgets
 * (WhatsApp floating button, Instagram embed script) so they never block the
 * first contentful paint or main thread on initial load.
 *
 * The flag flips either after `idle`/`load` fires, or after a short fallback
 * timeout (for browsers without requestIdleCallback), whichever comes first.
 */
export function useDeferredMount(delay = 1200): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let done = false;
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const trigger = () => {
      if (done) return;
      done = true;
      setReady(true);
    };

    const ric =
      typeof window !== "undefined"
        ? (window as any).requestIdleCallback
        : undefined;

    if (typeof ric === "function") {
      idleId = (window as any).requestIdleCallback(trigger, { timeout: delay });
      return () => {
        const cancel = (window as any).cancelIdleCallback;
        if (typeof cancel === "function" && idleId !== undefined)
          cancel(idleId);
      };
    }

    window.addEventListener("load", trigger, { once: true });
    timeoutId = setTimeout(trigger, delay);
    return () => {
      window.removeEventListener("load", trigger);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [delay]);

  return ready;
}
